import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  UserCheck, 
  Calendar, 
  CreditCard, 
  Heart, 
  Shield, 
  Star, 
  CheckCircle,
  ArrowRight,
  Clock,
  Phone,
  MessageCircle,
  MapPin,
  Users,
  Award,
  Eye,
  Lock
} from 'lucide-react';
import ScrollReveal from '../components/ui/ScrollReveal';
import ScrollRevealStack from '../components/ui/ScrollRevealStack';

const HowItWorks = () => {
  // SEO optimization - set document title and meta description
  useEffect(() => {
    document.title = 'How It Works - PetsFostering | Pet Care Marketplace Platform';
    
    // Create meta description if it doesn't exist
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Learn how PetsFostering works. Simple 4-step process to connect with local pet caregivers: Search, Connect, Arrange, and Manage. Platform connecting pet owners with independent caregivers.';

    // Add structured data for SEO
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Connect with Pet Caregivers on PetsFostering",
      "description": "Complete guide on how to find and connect with pet caregivers through PetsFostering marketplace platform",
      "image": "https://images.unsplash.com/photo-1601758228041-f3b2795255f1",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Browse Pet Caregivers",
          "text": "Browse and search for pet caregivers in your area using our platform filters for location, services, ratings, and availability.",
          "image": "https://images.unsplash.com/photo-1560807707-8cc77767d783",
          "url": "https://PetsFostering.app/search"
        },
        {
          "@type": "HowToStep", 
          "name": "Connect and Communicate",
          "text": "Chat with caregivers to discuss your needs, share details about your pet, and arrange service terms directly.",
          "image": "https://images.unsplash.com/photo-1544568100-847a948585b9",
          "url": "https://PetsFostering.app/messages"
        },
        {
          "@type": "HowToStep",
          "name": "Arrange Services",
          "text": "Agree on dates, services, and terms with your chosen caregiver. Schedule appointments through our platform.",
          "image": "https://images.unsplash.com/photo-1592754862816-1a21a4ea2281",
          "url": "https://PetsFostering.app/booking"
        },
        {
          "@type": "HowToStep",
          "name": "Manage and Review",
          "text": "Track your bookings through the platform and share your experience by leaving reviews for the community.",
          "image": "https://images.unsplash.com/photo-1548199973-03cce0bbc87b",
          "url": "https://PetsFostering.app/reviews"
        }
      ]
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);

    return () => {
      // Cleanup structured data on unmount
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const steps = [
    {
      id: 1,
      icon: Search,
      title: 'Browse Caregivers',
      subtitle: 'Explore Local Pet Caregivers',
      description: 'Search through local, independent pet caregivers in your neighborhood. Use our platform filters to find caregivers based on location, services offered, ratings, reviews, and availability. View detailed profiles with information about their experience and services.',
      features: [
        'Location-based search within your area',
        'Filter by services: pet sitting, dog walking, grooming, training',
        'Sort by ratings, reviews, and price',
        'View caregiver profiles with photos and experience',
        'Read community reviews from other pet owners',
        'Check availability and pricing'
      ],
      image: 'https://images.unsplash.com/photo-1560807707-8cc77767d783?auto=format&fit=crop&w=600&h=400&q=80',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50'
    },
    {
      id: 2,
      icon: MessageCircle,
      title: 'Connect & Discuss',
      subtitle: 'Chat With Caregivers Directly',
      description: 'Use our messaging platform to connect with caregivers directly. Discuss your pet\'s specific needs, ask questions, and get to know potential caregivers before arranging services. Communication happens directly between you and caregivers.',
      features: [
        'Direct messaging with caregivers',
        'Share photos and details about your pet',
        'Discuss services, rates, and availability',
        'Schedule meet-and-greets in person',
        'Ask questions about their experience',
        'Agree on terms that work for both parties'
      ],
      image: 'https://images.unsplash.com/photo-1544568100-847a948585b9?auto=format&fit=crop&w=600&h=400&q=80',
      color: 'from-blue-500 to-sky-500',
      bgColor: 'bg-[var(--pf-accent-soft)]'
    },
    {
      id: 3,
      icon: Calendar,
      title: 'Arrange Services',
      subtitle: 'Schedule and Agree on Terms',
      description: 'Once you\'ve found a good match, use the platform to arrange your bookings. Agree on schedule, pricing, and service details directly with the caregiver. The platform helps you organize and track your appointments.',
      features: [
        'Platform booking system for easy scheduling',
        'Calendar integration to track appointments',
        'Flexible arrangements based on your needs',
        'Direct agreements between you and caregiver',
        'Add special instructions and requirements',
        'Share emergency contact information'
      ],
      image: 'https://images.unsplash.com/photo-1592754862816-1a21a4ea2281?auto=format&fit=crop&w=600&h=400&q=80',
      color: 'from-green-500 to-teal-500',
      bgColor: 'bg-green-50'
    },
    {
      id: 4,
      icon: Heart,
      title: 'Manage & Review',
      subtitle: 'Track Bookings and Share Feedback',
      description: 'Keep track of your pet care arrangements through the platform. After services are complete, share your experience by leaving reviews to help other pet owners in the community make informed decisions.',
      features: [
        'Track your bookings in one place',
        'Receive updates and communicate as needed',
        'Maintain service history and notes',
        'Rate and review your experience',
        'Help build community trust through feedback',
        'Easily rebook for future services'
      ],
      image: 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=600&h=400&q=80',
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50'
    }
  ];

  const safetyFeatures = [
    {
      icon: Search,
      title: 'Browse Profiles',
      description: 'View detailed caregiver profiles with experience, services offered, and photos to help you find the right match for your pet.'
    },
    {
      icon: MessageCircle,
      title: 'Direct Communication',
      description: 'Chat directly with caregivers through our secure messaging system before making any arrangements.'
    },
    {
      icon: Star,
      title: 'Community Reviews',
      description: 'Read reviews and ratings from other pet owners to help inform your decision and build trust in the community.'
    },
    {
      icon: Users,
      title: 'Platform Support',
      description: 'Our support team is available to help with platform features, technical issues, and using the booking system.'
    }
  ];

  const servicesOffered = [
    {
      name: 'Pet Sitting',
      description: 'In-home pet care services from local caregivers',
      duration: 'Daily visits or overnight stays',
      price: 'From ₹500/day'
    },
    {
      name: 'Dog Walking',
      description: 'Exercise and outdoor time for your dogs',
      duration: '30-60 minutes per walk',
      price: 'From ₹200/walk'
    },
    {
      name: 'Pet Boarding',
      description: 'Overnight stays in caregiver homes',
      duration: 'Overnight to extended stays',
      price: 'From ₹800/night'
    },
    {
      name: 'Pet Grooming',
      description: 'Grooming and hygiene services',
      duration: '1-3 hours per session',
      price: 'From ₹1000/session'
    }
  ];

  // Stats hidden - will enable when we have actual data
  // const stats = [
  //   { number: '50,000+', label: 'Happy Pet Parents', icon: Users },
  //   { number: '1,000+', label: 'Caregivers', icon: Shield },
  //   { number: '4.9/5', label: 'Average Rating', icon: Star },
  //   { number: '100K+', label: 'Connections Made', icon: CheckCircle }
  // ];

  return (
    <div className="pf-page-wrap">
      <header className="pf-section-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="left" className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              How PetsFostering Works: Connect with Local Pet Caregivers
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
              Discover how easy it is to find local, independent pet caregivers in your area. 
              Our simple 4-step process connects pet owners with caregivers in their community 
              through a convenient marketplace platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/search" 
                className="pf-btn-primary px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2"
                aria-label="Start searching for pet caregivers in your area"
              >
                <Search className="w-5 h-5" />
                Find caregivers now
              </Link>
              <Link 
                to="/signup" 
                className="pf-btn-community px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2"
                aria-label="Join PetsFostering as a pet parent or caregiver"
              >
                <Heart className="w-5 h-5" />
                Join our community
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </header>

      {/* How It Works Steps */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <ScrollReveal direction="right" className="text-center mb-16" aria-labelledby="steps-heading">
          <h2 id="steps-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Simple 4-Step Process to Connect with Caregivers
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From browsing local caregivers to managing your bookings, our platform makes it easy to connect 
            with independent pet caregivers in your community.
          </p>
        </ScrollReveal>

        <div className="space-y-24">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isEven = index % 2 === 0;
            
            return (
              <ScrollReveal key={step.id} direction={isEven ? 'left' : 'right'} delayMs={index * 90}>
              <article className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
                <div className="flex-1 space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[var(--pf-accent)] text-white rounded-full flex items-center justify-center font-bold text-xl">
                      {step.id}
                    </div>
                    <div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900">{step.title}</h3>
                      <p className="text-lg text-[var(--pf-accent)] font-semibold">{step.subtitle}</p>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {step.description}
                  </p>

                  <div className={`p-6 ${step.bgColor} rounded-xl`}>
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Icon className="w-5 h-5" />
                      Key Features:
                    </h4>
                    <ul className="space-y-2">
                      {step.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2 text-gray-700">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex-1">
                  <div className="relative">
                    <img 
                      src={step.image} 
                      alt={`Step ${step.id}: ${step.title} - ${step.subtitle}`}
                      className="w-full h-80 object-cover rounded-2xl shadow-xl"
                      loading="lazy"
                    />
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-[var(--pf-accent)] text-white rounded-full flex items-center justify-center shadow-lg">
                      <Icon className="w-8 h-8" />
                    </div>
                  </div>
                </div>
              </article>
              </ScrollReveal>
            );
          })}
        </div>
      </main>

      {/* Services Section */}
      <section className="bg-white py-16" aria-labelledby="services-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="left" className="text-center mb-12">
            <h2 id="services-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Pet Care Services Available on the Platform
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Connect with local caregivers offering a variety of pet care services to meet your needs.
            </p>
          </ScrollReveal>

          <ScrollRevealStack className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" staggerMs={105}>
            {servicesOffered.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.name}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{service.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    <span className="font-semibold text-[var(--pf-accent)]">{service.price}</span>
                  </div>
                </div>
              </div>
            ))}
          </ScrollRevealStack>
        </div>
      </section>

      {/* Safety & Trust Section */}
      <section className="py-16 bg-gray-50" aria-labelledby="safety-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="right" className="text-center mb-12">
            <h2 id="safety-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Platform Features to Help You Connect
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our platform provides tools to help you browse caregivers, communicate, and manage bookings 
              all in one convenient place.
            </p>
          </ScrollReveal>

          <ScrollRevealStack className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" staggerMs={105}>
            {safetyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </ScrollRevealStack>
        </div>
      </section>

      {/* Stats Section - Hidden until we have actual data */}
      {/* <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white" aria-labelledby="stats-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="stats-heading" className="text-3xl md:text-4xl font-bold text-center mb-12">
            Growing Community of Pet Owners and Caregivers
          </h2>
        </div>
      </section> */}

      {/* Disclaimer Section */}
      <section className="py-12 bg-gray-100 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="left">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Shield className="w-6 h-6 text-[var(--pf-accent)]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Platform Disclaimer</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  PetsFostering is a marketplace platform that connects pet owners with independent caregivers. We facilitate bookings and provide communication tools, but we do not employ caregivers or provide pet care services directly.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  All arrangements, agreements, and services are between pet owners and caregivers. Users are responsible for conducting their own due diligence, including verifying credentials, meeting in person, and agreeing on terms. PetsFostering is not liable for the quality of services, incidents, or disputes between users.
                </p>
              </div>
            </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white" aria-labelledby="cta-heading">
        <ScrollReveal direction="right" className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 id="cta-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Connect with Local Pet Caregivers?
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Join our marketplace platform to discover local caregivers in your area and manage your pet care bookings conveniently.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/search" 
              className="pf-btn-primary px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2"
              aria-label="Browse and book pet caregivers in your area"
            >
              <Search className="w-5 h-5" />
              Browse caregivers
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link 
              to="/signup" 
              className="pf-btn-community px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2"
              aria-label="Create your PetsFostering account"
            >
              <UserCheck className="w-5 h-5" />
              Join our community
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default HowItWorks;
